import { Injectable } from '@angular/core';
import { Invitation } from '../../invitations/models/invitation.model';
import { InvitationMockService } from '../../invitations/services/invitation-mock.service';
import {
  AssessmentRegistration, AssessmentRun, AssessmentStage, AssessmentStatement, ScaleValue, Stage,
} from '../models/assessment.model';
import { STATEMENTS_SEED } from '../data/assessment.seed';

const STORAGE_KEY = 'fs_mock_assessment_v1';
const NEVER = '1970-01-01T00:00:00Z'; // deterministic placeholder; replaced by API later

@Injectable({ providedIn: 'root' })
export class AssessmentMockService {
  readonly statements: AssessmentStatement[] = [...STATEMENTS_SEED].sort((a, b) => a.order - b.order);
  private runs: Record<string, AssessmentRun>;

  constructor(private invitations: InvitationMockService) {
    const saved = localStorage.getItem(STORAGE_KEY);
    this.runs = saved ? JSON.parse(saved) : {};
  }

  /** Read-only lookup; `of()` emits synchronously so this is safe. */
  invitationFor(token: string): Invitation | undefined {
    let inv: Invitation | undefined;
    this.invitations.getById(token).subscribe(i => inv = i).unsubscribe();
    return inv;
  }

  getRun(token: string): AssessmentRun | undefined { return this.runs[token]; }

  stageFor(token: string): AssessmentStage {
    const run = this.runs[token];
    if (run?.status === 'complete') { return 'thanks'; }
    const inv = this.invitationFor(token);
    if (!inv && !run) { return 'invalid'; }
    if (inv && (inv.archived || inv.status === 'closed') && !run) { return 'closed'; }
    if (!run?.registration) { return 'landing'; }
    if (run.paid === false) { return 'pay'; }
    return 'questions';
  }

  register(token: string, registration: AssessmentRegistration): void {
    const inv = this.invitationFor(token);
    this.runs[token] = {
      token, registration,
      paid: inv?.whoPays === 'participant_pays' ? false : 'exempt',
      answers: {}, currentIndex: 0, status: 'registered',
    };
    this.persist();
  }

  recordPayment(token: string): void {
    const run = this.must(token);
    run.paid = true;
    this.persist();
  }

  answer(token: string, statementId: string, value: ScaleValue): void {
    const run = this.must(token);
    run.answers[statementId] = value;
    run.status = 'in_progress';
    run.currentIndex = this.firstUnansweredIndex(run);
    this.persist();
  }

  firstUnansweredIndex(run: AssessmentRun): number {
    const i = this.statements.findIndex(s => run.answers[s.id] == null);
    return i === -1 ? this.statements.length : i;
  }

  /**
   * Mark the run complete. Deliberately does no scoring: this is a questionnaire — the answers
   * go to the backend and the profile comes back from it. Nothing here should compute a result.
   */
  complete(token: string): AssessmentRun {
    const run = this.must(token);
    if (this.firstUnansweredIndex(run) < this.statements.length) {
      throw new Error('Cannot complete with unanswered statements');
    }
    run.status = 'complete';
    run.completedAt = NEVER;
    this.persist();
    return run;
  }

  private must(token: string): AssessmentRun {
    const run = this.runs[token];
    if (!run) { throw new Error(`No assessment run for token ${token}`); }
    return run;
  }

  private persist(): void { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.runs)); }
}
